import React, { Dispatch, PropsWithChildren, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { navigate } from 'gatsby';
import { getSelectedFacility } from 'src/apollo/local-state';
import { usePrevious } from 'src/hooks/use-previous';
import { isBrowser, UserVenue } from 'src/utils/auth/types/auth.types';

export interface AppContext {
  selectedFacility: UserVenue | null;
  tenantSettings: UserVenue['tenantSettings'] | null;
}

const StateContext = React.createContext<AppContext | undefined>(undefined);
const DispatchContext = React.createContext<Dispatch<SetStateAction<AppContext>> | undefined>(undefined);

const AppContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const client = useApolloClient();
  const [appContext, setAppContext] = useState(() => {
    const defaultFacility = getSelectedFacility(client);
    const tenantSettings = defaultFacility?.tenantSettings;

    return {
      selectedFacility: defaultFacility,
      tenantSettings: tenantSettings ?? null,
    };
  });
  const previousFacilityId = usePrevious(appContext.selectedFacility?.id);

  useEffect(() => {
    if (previousFacilityId && previousFacilityId !== appContext.selectedFacility?.id) {
      navigate('/');
    }
  }, [previousFacilityId, appContext]);

  return (
    <DispatchContext.Provider value={setAppContext}>
      <StateContext.Provider value={appContext}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(StateContext);

  if (!isBrowser) return {} as AppContext;

  if (!context) throw new Error('useAppContext must be used within an AppContextProvider');

  return context;
};

const dummyDispatch = () => {};

export const useSetAppContext = () => {
  const context = useContext(DispatchContext);

  if (!isBrowser) return dummyDispatch as Dispatch<SetStateAction<AppContext>>;

  if (!context) throw new Error('useSetAppContext must be used within an AppContextProvider');

  return context;
};

export default AppContextProvider;
